"use client";

import cnBind from "classnames/bind";
import { useTranslation } from "react-i18next";

import { Logo } from "@/components/Logo";
import { TelegaIcon } from "@/shared/assests/svg/svg";

import {
    FOOTER_INSTAGRAM_HREF,
    FOOTER_LEGAL,
    FOOTER_PHONE,
    FOOTER_PHONE_HREF,
    FOOTER_PRODUCT_HREFS,
    FOOTER_SOLUTION_HREFS,
    FOOTER_TELEGRAM_HREF,
} from "./footer.constants";
import styles from "./Footer.module.scss";

const cx = cnBind.bind(styles);

const InstagramIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="17.2" cy="6.8" r="1.1" fill="currentColor" />
    </svg>
);

type FooterProps = {
    /** Extra bottom padding so fixed IndustryPresetBar does not cover legal links */
    clearFixedBar?: boolean;
};

type LinkItem = { label: string; key: string };

export const Footer = ({ clearFixedBar = false }: FooterProps) => {
    const { t } = useTranslation("common");
    const productLinks = t("home.footer.productLinks", { returnObjects: true }) as LinkItem[];
    const solutionLinks = t("home.footer.solutionLinks", { returnObjects: true }) as LinkItem[];
    const legalLines = t("home.footer.legal.lines", { returnObjects: true }) as string[];

    return (
        <footer className={cx("footer", { clearFixedBar })}>
            <div className={cx("wrapper", "container")}>
                <div className={cx("grid")}>
                    <div className={cx("column", "brandColumn")}>
                        <Logo isFooter />
                        <p className={cx("tagline")}>{t("home.footer.tagline")}</p>
                        <div className={cx("social")}>
                            <a
                                className={cx("socialLink")}
                                href={FOOTER_TELEGRAM_HREF}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Telegram Tezam"
                            >
                                <TelegaIcon className={cx("socialIcon")} />
                            </a>
                            <a
                                className={cx("socialLink")}
                                href={FOOTER_INSTAGRAM_HREF}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Instagram Tezam"
                            >
                                <InstagramIcon />
                            </a>
                        </div>
                    </div>

                    <nav className={cx("column")} aria-label={t("home.footer.columns.products")}>
                        <h3 className={cx("columnTitle")}>{t("home.footer.columns.products")}</h3>
                        <ul className={cx("links")}>
                            {(Array.isArray(productLinks) ? productLinks : []).map((item) => (
                                <li key={item.key}>
                                    <a className={cx("link")} href={FOOTER_PRODUCT_HREFS[item.key] ?? "#"}>
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav className={cx("column")} aria-label={t("home.footer.columns.solutions")}>
                        <h3 className={cx("columnTitle")}>{t("home.footer.columns.solutions")}</h3>
                        <ul className={cx("links")}>
                            {(Array.isArray(solutionLinks) ? solutionLinks : []).map((item) => (
                                <li key={item.key}>
                                    <a className={cx("link")} href={FOOTER_SOLUTION_HREFS[item.key] ?? "#"}>
                                        {item.label}
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <div className={cx("column")}>
                        <h3 className={cx("columnTitle")}>{t("home.footer.columns.contacts")}</h3>
                        <ul className={cx("links", "contacts")}>
                            <li>
                                <a className={cx("link", "phone")} href={FOOTER_PHONE_HREF}>
                                    {FOOTER_PHONE}
                                </a>
                            </li>
                            <li>
                                <a
                                    className={cx("telegramCta")}
                                    href={FOOTER_TELEGRAM_HREF}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {t("home.footer.telegramCta")}
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className={cx("paymentMethods")}>
                    <img
                        src="/images/payment-methods-horizontal-white.png"
                        alt={t("home.footer.paymentMethodsAlt")}
                        width={720}
                        height={40}
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                <div className={cx("legal")}>
                    <p className={cx("copyright")}>{t("home.footer.legal.copyright")}</p>
                    <div className={cx("legalMeta")}>
                        {(Array.isArray(legalLines) ? legalLines : []).map((line) => (
                            <p key={line}>{line}</p>
                        ))}
                    </div>
                    <div className={cx("legalLinks")}>
                        <a className={cx("legalLink")} href={FOOTER_LEGAL.paymentInfoHref}>
                            {t("home.footer.legal.paymentInfo")}
                        </a>
                        <span className={cx("legalDot")} aria-hidden>
                            •
                        </span>
                        <a className={cx("legalLink")} href={FOOTER_LEGAL.policyHref}>
                            {t("home.footer.legal.policy")}
                        </a>
                        <span className={cx("legalDot")} aria-hidden>
                            •
                        </span>
                        <a
                            className={cx("legalLink")}
                            href={FOOTER_LEGAL.offerHref}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t("home.footer.legal.offer")}
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};
